'use client';
import React from 'react';
import Link from 'next/link';

export default function Footer() {
  return (
    <footer className="bg-[#0F0F0F] text-white">
      {/* CTA Banner */}
      <div className="border-b border-white/10">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-12 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
          <div className="max-w-2xl">
            <span className="font-mono text-xs text-[#CC0000] tracking-[0.2em] block mb-3">[ GET IN TOUCH ]</span>
            <h3 className="text-3xl sm:text-4xl font-extrabold text-white font-heading tracking-tight leading-[1.1]">
              Ready to <span className="text-[#CC0000]">work with us?</span>
            </h3>
          </div>
          <Link
            href="/careers"
            className="inline-flex items-center gap-2 bg-[#CC0000] text-white pl-6 pr-2 py-3 rounded-sm font-semibold hover:bg-[#AA0000] transition-colors group flex-shrink-0"
          >
            Explore Careers
            <span className="bg-white/15 group-hover:bg-white/25 p-2 rounded-sm transition-colors">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M7 7h10v10" />
                <path d="M7 17 17 7" />
              </svg>
            </span>
          </Link>
        </div>
      </div>

      {/* Main footer */}
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-10 mb-12">
          {/* Brand column */}
          <div className="lg:col-span-4">
            <Link href="/" className="flex items-center gap-3 mb-6">
              <div className="w-11 h-11 bg-[#CC0000] flex items-center justify-center text-white font-extrabold text-xl rounded-sm">U</div>
              <span className="text-white font-extrabold text-lg tracking-tight">UNOC<sup className="text-[10px] font-bold ml-0.5">®</sup></span>
            </Link>
            <p className="text-gray-400 text-sm leading-relaxed mb-6 max-w-sm">
              Uganda National Oil Company — driving the country&apos;s petroleum sector across the full value chain, from exploration to refined products.
            </p>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-[#CC0000] mt-1 flex-shrink-0" aria-hidden="true">
                  <path d="M20 10c0 4.993-5.539 10.193-7.399 11.799a1 1 0 0 1-1.202 0C9.539 20.193 4 14.993 4 10a8 8 0 0 1 16 0" />
                  <circle cx="12" cy="10" r="3" />
                </svg>
                <p className="text-gray-400 text-sm leading-relaxed">
                  Plot 15 Yusuf Lule Rd, Kampala, Uganda<br />
                  P.O Box 36316, Kampala
                </p>
              </div>
              <div className="flex items-center gap-3">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-[#CC0000] flex-shrink-0" aria-hidden="true">
                  <path d="M13.832 16.568a1 1 0 0 0 1.213-.303l.355-.465A2 2 0 0 1 17 15h3a2 2 0 0 1 2 2v3a2 2 0 0 1-2 2A18 18 0 0 1 2 4a2 2 0 0 1 2-2h3a2 2 0 0 1 2 2v3a2 2 0 0 1-.8 1.6l-.468.351a1 1 0 0 0-.292 1.233 14 14 0 0 0 6.392 6.384" />
                </svg>
                <a href="tel:+256312444600" className="text-gray-400 text-sm hover:text-[#CC0000] transition-colors">+256 312 444 600</a>
              </div>
              <div className="flex items-center gap-3">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-[#CC0000] flex-shrink-0" aria-hidden="true">
                  <path d="m22 7-8.991 5.727a2 2 0 0 1-2.009 0L2 7" />
                  <rect x="2" y="4" width="20" height="16" rx="2" />
                </svg>
                <a href="mailto:info.unoc@unoc.co.ug" className="text-gray-400 text-sm hover:text-[#CC0000] transition-colors">info.unoc@unoc.co.ug</a>
              </div>
            </div>
          </div>

          {/* Business */}
          <div className="lg:col-span-2">
            <h4 className="text-[10px] font-bold uppercase tracking-[0.2em] mb-5 text-[#CC0000] font-heading">
              <span className="font-mono mr-1">01</span> Business
            </h4>
            <ul className="space-y-3">
              <li><Link href="/projects" className="text-gray-400 hover:text-white transition-colors duration-200 text-sm">Upstream Projects</Link></li>
              <li><Link href="/projects/eacop" className="text-gray-400 hover:text-white transition-colors duration-200 text-sm">Midstream Projects</Link></li>
              <li><Link href="/projects/refinery" className="text-gray-400 hover:text-white transition-colors duration-200 text-sm">Downstream Operations</Link></li>
              <li><Link href="/about" className="text-gray-400 hover:text-white transition-colors duration-200 text-sm">Subsidiaries (NPC / UEL)</Link></li>
            </ul>
          </div>

          {/* Resources */}
          <div className="lg:col-span-2">
            <h4 className="text-[10px] font-bold uppercase tracking-[0.2em] mb-5 text-[#CC0000] font-heading">
              <span className="font-mono mr-1">02</span> Resources
            </h4>
            <ul className="space-y-3">
              <li><Link href="/resources" className="text-gray-400 hover:text-white transition-colors duration-200 text-sm">Annual Reports</Link></li>
              <li><Link href="/resources" className="text-gray-400 hover:text-white transition-colors duration-200 text-sm">Strategic Plan</Link></li>
              <li><Link href="/resources" className="text-gray-400 hover:text-white transition-colors duration-200 text-sm">Resource Center</Link></li>
              <li><Link href="/news" className="text-gray-400 hover:text-white transition-colors duration-200 text-sm">Media Gallery</Link></li>
            </ul>
          </div>

          {/* Transparency */}
          <div className="lg:col-span-2">
            <h4 className="text-[10px] font-bold uppercase tracking-[0.2em] mb-5 text-[#CC0000] font-heading">
              <span className="font-mono mr-1">03</span> Transparency
            </h4>
            <ul className="space-y-3">
              <li><Link href="/sustainability/national-content" className="text-gray-400 hover:text-white transition-colors duration-200 text-sm">National Content</Link></li>
              <li><Link href="/sustainability/hse" className="text-gray-400 hover:text-white transition-colors duration-200 text-sm">Health, Safety &amp; Environment</Link></li>
              <li><Link href="/careers" className="text-gray-400 hover:text-white transition-colors duration-200 text-sm">Careers</Link></li>
              <li><Link href="/faqs" className="text-gray-400 hover:text-white transition-colors duration-200 text-sm">Access to Information</Link></li>
            </ul>
          </div>

          {/* Affiliates */}
          <div className="lg:col-span-2">
            <h4 className="text-[10px] font-bold uppercase tracking-[0.2em] mb-5 text-[#CC0000] font-heading">
              <span className="font-mono mr-1">04</span> Affiliates
            </h4>
            <ul className="space-y-3">
              <li><a href="https://www.energyandminerals.go.ug" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition-colors duration-200 text-sm">Ministry of Energy and Mineral Development</a></li>
              <li><a href="https://www.finance.go.ug" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition-colors duration-200 text-sm">Ministry of Finance, Planning and Economic Development</a></li>
              <li><a href="https://www.pau.go.ug" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition-colors duration-200 text-sm">Petroleum Authority of Uganda</a></li>
              <li><a href="https://www.nema.go.ug" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition-colors duration-200 text-sm">National Environment Management Authority</a></li>
            </ul>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="border-t border-white/10 pt-8 flex flex-col md:flex-row justify-between items-center gap-5">
          <p className="text-gray-500 text-xs font-mono tracking-wider">© 2026 UGANDA NATIONAL OIL COMPANY. ALL RIGHTS RESERVED.</p>
          <div className="flex items-center gap-3">
            <a href="https://www.facebook.com/UNOCLimited" target="_blank" rel="noopener noreferrer" aria-label="Facebook" className="w-9 h-9 bg-white/5 hover:bg-[#CC0000] flex items-center justify-center rounded-sm transition-colors">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-white" aria-hidden="true">
                <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" />
              </svg>
            </a>
            <a href="https://www.linkedin.com/company/uganda-national-oil-company-limited" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn" className="w-9 h-9 bg-white/5 hover:bg-[#CC0000] flex items-center justify-center rounded-sm transition-colors">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-white" aria-hidden="true">
                <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z" />
                <rect x="2" y="9" width="4" height="12" />
                <circle cx="4" cy="4" r="2" />
              </svg>
            </a>
            <a href="https://twitter.com/UNOC_LTD" target="_blank" rel="noopener noreferrer" aria-label="Twitter" className="w-9 h-9 bg-white/5 hover:bg-[#CC0000] flex items-center justify-center rounded-sm transition-colors">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-white" aria-hidden="true">
                <path d="M22 4s-.7 2.1-2 3.4c1.6 10-9.4 17.3-18 11.6 2.2.1 4.4-.6 6-2C3 15.5.5 9.6 3 5c2.2 2.6 5.6 4.1 9 4-.9-4.2 4-6.6 7-3.8 1.1 0 3-1.2 3-1.2z" />
              </svg>
            </a>
            <a href="https://www.youtube.com/@UNOCLimited" target="_blank" rel="noopener noreferrer" aria-label="YouTube" className="w-9 h-9 bg-white/5 hover:bg-[#CC0000] flex items-center justify-center rounded-sm transition-colors">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-white" aria-hidden="true">
                <path d="M2.5 17a24.12 24.12 0 0 1 0-10 2 2 0 0 1 1.4-1.4 49.56 49.56 0 0 1 16.2 0A2 2 0 0 1 21.5 7a24.12 24.12 0 0 1 0 10 2 2 0 0 1-1.4 1.4 49.55 49.55 0 0 1-16.2 0A2 2 0 0 1 2.5 17" />
                <path d="m10 15 5-3-5-3z" />
              </svg>
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}
